const backgroundGradients = ({
  minStepVh = 40, // minimum scroll change (in vh) before updating target
  easeDurationMs = 180, // how quickly to ease toward the target
}: {
  minStepVh?: number;
  easeDurationMs?: number;
} = {}): void => {
  const selector = '[data-background-gradient]';
  const els = Array.from(document.querySelectorAll<HTMLElement>(selector));
  if (els.length === 0) return;

  const N = els.length;
  const segment = N > 1 ? 1 / (N - 1) : 1;

  // State
  let ticking = false;
  let animating = false;
  let lastTs = 0;

  // Scroll fraction states: current is rendered; target moves in gated steps
  let currentF = 0;
  let targetF = 0;

  const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

  const rawScrollFraction = () => {
    const scrollTop = window.scrollY;
    const docHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    return docHeight > 0 ? clamp01(scrollTop / docHeight) : 0;
  };

  const minStepFraction = () => {
    const px = (minStepVh / 100) * window.innerHeight; // vh → px
    const docHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    return docHeight > 0 ? px / docHeight : 1; // if no scrollable area, treat as full
  };

  const render = (f: number) => {
    els.forEach((el, i) => {
      if (N === 1) {
        el.style.setProperty('--opacity', '1');
        return;
      }
      const anchor = i * segment;
      const dist = Math.abs(f - anchor);
      const weight = Math.max(0, 1 - dist / segment); // triangular falloff
      el.style.setProperty('--opacity', weight.toFixed(3));
    });
  };

  const easeToward = (from: number, to: number, dtMs: number) => {
    // Exponential ease by duration target (stable across FPS)
    // alpha ~ fraction of remaining distance to cover this frame
    const alpha = 1 - Math.exp(-dtMs / easeDurationMs);
    return from + (to - from) * alpha;
  };

  const animate = (ts: number) => {
    const dt = Math.max(0, ts - lastTs);
    lastTs = ts;

    // Ease currentF toward targetF
    const nextF = easeToward(currentF, targetF, dt);
    currentF = nextF;
    render(currentF);

    // Continue until close
    if (Math.abs(targetF - currentF) > 0.001) {
      requestAnimationFrame(animate);
    } else {
      currentF = targetF; // snap the last tiny bit
      render(currentF);
      animating = false;
    }
  };

  const updateTargetIfPastDeadband = () => {
    const f = rawScrollFraction();
    const stepF = minStepFraction();

    // Only move the target if we've scrolled far enough since last target
    if (Math.abs(f - targetF) >= stepF) {
      targetF = f;
      // Kick animation loop if needed
      if (!animating) {
        animating = true;
        lastTs = performance.now();
        requestAnimationFrame(animate);
      }
    }
  };

  const onScrollOrResize = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        // Re-check target gating on each scroll/resize
        updateTargetIfPastDeadband();
      });
    }
  };

  // Initial paint
  currentF = targetF = rawScrollFraction();
  render(currentF);

  // Listeners
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', () => {
    // Recalculate step size implicitly (uses innerHeight in minStepFraction)
    onScrollOrResize();
  });
};

export default backgroundGradients;
